import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { parseCsvRows } from '@crowdex/plugin-contracts';
import { MessageResponse } from '@crowdex/ui/ai/message';
import { Button } from '@crowdex/ui/components/button';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { Textarea } from '@crowdex/ui/components/textarea';
import { toast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import { Pencil } from 'lucide-react';
import { type ComponentProps, useEffect, useMemo, useState } from 'react';
import { basename } from '@/lib/desktop-paths';
import { type ArtifactContent, artifactContent, saveArtifactContent } from '../artifacts-client';
import {
  csvSeparator,
  editableInViewer,
  mediaTypeOf,
  setTask,
  splitFrontMatter,
  taskLines,
  viewerOf,
} from './viewer-model';
import { MAX_ROWS, readXlsx, type Sheet } from './xlsx';

/**
 * The common viewers (brand/crow-extensions.html, "Visionneuses communes"), which the host provides
 * once for every extension: Markdown (rendered, its tasks ticked and saved, an edit mode), image,
 * PDF, video and table (CSV, XLSX). A file whose type has no view of its extension, or a plain file
 * of one of these media types, opens here, in the host's frame; audio, a web page (sandboxed) and text
 * too, anything else is a download.
 */

export type ViewedFile = Pick<
  ArtifactListingEntry,
  'space' | 'entry' | 'plugin' | 'conversation' | 'title' | 'edit'
>;

const bytesOf = (content: ArtifactContent) =>
  content.encoding === 'base64'
    ? Uint8Array.from(atob(content.content), (character) => character.charCodeAt(0))
    : new TextEncoder().encode(content.content);

/** A local URL for a file's bytes, revoked when the viewer goes away. */
function useObjectUrl(content: ArtifactContent | null, mediaType: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (content === null || mediaType === undefined) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(new Blob([bytesOf(content)], { type: mediaType }));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [content, mediaType]);
  return url;
}

/**
 * A file in its common viewer. `compact`: a preview (a card, a thumbnail), read-only and short;
 * `playable`: a video plays there with its controls (a render in the conversation).
 */
export function CommonViewer({
  file,
  compact = false,
  playable = false,
}: {
  file: ViewedFile;
  compact?: boolean;
  playable?: boolean;
}) {
  const [content, setContent] = useState<ArtifactContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { space, entry: path, plugin, conversation } = file;
  useEffect(() => {
    let live = true;
    setContent(null);
    setError(null);
    artifactContent({ space, path, plugin, conversation }).then(
      (value) => live && setContent(value),
      (reason: unknown) =>
        live && setError(reason instanceof Error ? reason.message : String(reason)),
    );
    return () => {
      live = false;
    };
  }, [space, path, plugin, conversation]);
  const kind = viewerOf(path);
  const mediaType = mediaTypeOf(path);
  const binary =
    kind === 'image' ||
    kind === 'pdf' ||
    kind === 'video' ||
    kind === 'audio' ||
    kind === 'download';
  const url = useObjectUrl(binary ? content : null, mediaType ?? 'application/octet-stream');
  if (error !== null)
    return compact ? null : (
      <InlineMessage title="This file is unavailable." tone="error">
        {error}
      </InlineMessage>
    );
  if (content === null || (binary && url === null))
    return <div aria-busy="true" className="size-full" data-common-viewer={kind} />;
  switch (kind) {
    case 'image':
      return (
        <img
          alt={file.title}
          className="size-full object-contain"
          data-common-viewer="image"
          src={url ?? undefined}
        />
      );
    case 'video':
      return (
        <video
          className="size-full bg-black object-contain"
          controls={!compact || playable}
          data-common-viewer="video"
          muted={compact}
          playsInline
          preload="metadata"
          src={url ?? undefined}
        >
          <track kind="captions" />
        </video>
      );
    case 'pdf':
      return (
        <iframe
          className="size-full border-0 bg-white"
          data-common-viewer="pdf"
          src={url ?? undefined}
          title={file.title}
        />
      );
    case 'audio':
      return (
        <div className="flex size-full items-center justify-center p-4" data-common-viewer="audio">
          <audio className="w-full" controls preload="metadata" src={url ?? undefined}>
            <track kind="captions" />
          </audio>
        </div>
      );
    case 'download':
      return (
        <div className="flex size-full flex-col items-center justify-center gap-2 p-4 text-center">
          <p className="text-muted-foreground text-xs">{path}</p>
          {!compact && (
            <a
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
              download={basename(path)}
              href={url ?? undefined}
            >
              Download
            </a>
          )}
        </div>
      );
    case 'table':
      return <TableViewer compact={compact} content={content} path={path} />;
    default:
      break;
  }
  if (content.encoding === 'base64')
    return <p className="p-4 text-muted-foreground text-xs">Binary file ({path}).</p>;
  if (kind === 'html')
    return (
      <iframe
        className="size-full border-0 bg-white"
        sandbox="allow-scripts"
        srcDoc={content.content}
        title={file.title}
      />
    );
  if (kind === 'markdown') {
    // What the host may write: the user's file (not what a plugin ships), as it is now.
    const writable = space === 'plugin' ? null : space;
    const editable =
      !compact &&
      writable !== null &&
      content.reported !== true &&
      (file.edit || plugin === 'core') &&
      editableInViewer(path);
    return (
      <MarkdownViewer
        compact={compact}
        onSave={
          editable && writable !== null
            ? async (next) => {
                const saved = await saveArtifactContent({
                  space: writable,
                  path,
                  plugin,
                  conversation,
                  content: next,
                  version: content.version,
                });
                setContent({ ...content, content: next, version: saved.version });
              }
            : undefined
        }
        source={content.content}
      />
    );
  }
  return (
    <pre
      className={cn(
        'overflow-auto whitespace-pre-wrap p-4 font-mono',
        compact ? 'type-caption' : 'text-xs',
      )}
    >
      {compact ? content.content.slice(0, 2000) : content.content}
    </pre>
  );
}

/** A task's checkbox: ticked as the text says; the viewer handles clicks (see `MarkdownViewer`). */
function TaskCheckbox({
  checked,
  type,
  node: _node,
  ...props
}: ComponentProps<'input'> & { node?: unknown }) {
  if (type !== 'checkbox') return <input type={type} {...props} />;
  return (
    <input
      aria-label={checked ? 'Done' : 'To do'}
      checked={checked === true}
      className="mr-1.5 align-middle"
      data-markdown-task
      onChange={() => undefined}
      type="checkbox"
    />
  );
}

/** Stable, as `MessageResponse` re-renders on its text only. */
const MARKDOWN_COMPONENTS = { input: TaskCheckbox };

/**
 * Markdown, rendered (without its front matter): a task ticked is saved at once; Edit opens its
 * text, saved as a whole. Read-only without `onSave`.
 */
export function MarkdownViewer({
  source,
  compact = false,
  onSave,
}: {
  source: string;
  compact?: boolean;
  onSave?: ((next: string) => Promise<void>) | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(source);
  const [busy, setBusy] = useState(false);
  const { frontMatter, body } = splitFrontMatter(source);
  const save = async (next: string) => {
    if (onSave === undefined || busy) return false;
    setBusy(true);
    try {
      await onSave(next);
      return true;
    } catch (error) {
      toast.error('Not saved', error instanceof Error ? error.message : String(error));
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (editing)
    return (
      <div className="flex size-full flex-col gap-2 p-4" data-common-viewer="markdown">
        <Textarea
          aria-label="Markdown"
          className="min-h-0 flex-1 font-mono text-xs"
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          spellCheck={false}
          value={draft}
        />
        <div className="flex justify-end gap-2">
          <Button onClick={() => setEditing(false)} size="sm" type="button" variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={busy || draft === source}
            onClick={() =>
              void save(`${draft.trimEnd()}\n`).then((saved) => saved && setEditing(false))
            }
            size="sm"
            type="button"
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    );
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the clicks are its checkboxes', delegated.
    // biome-ignore lint/a11y/useKeyWithClickEvents: a checkbox is reached and toggled with the keyboard itself.
    <div
      className={cn(
        'relative size-full',
        compact ? 'overflow-hidden p-3 text-xs' : 'overflow-auto p-6',
      )}
      data-common-viewer="markdown"
      data-editable={onSave === undefined ? undefined : ''}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (!(target instanceof HTMLInputElement) || !target.matches('[data-markdown-task]'))
          return;
        event.preventDefault();
        if (onSave === undefined) return;
        const boxes = [...event.currentTarget.querySelectorAll('input[data-markdown-task]')];
        const index = boxes.indexOf(target);
        const task = taskLines(body)[index];
        if (task === undefined) return;
        // The text says what it is (the box has already flipped under the click).
        void save(`${frontMatter}${setTask(body, index, !task.done)}`);
      }}
    >
      {onSave !== undefined && (
        <Button
          className="absolute top-3 right-3 z-10"
          onClick={() => {
            setDraft(source);
            setEditing(true);
          }}
          size="sm"
          type="button"
          variant="outline"
        >
          <Pencil className="size-3.5" />
          Edit
        </Button>
      )}
      <MessageResponse components={MARKDOWN_COMPONENTS}>
        {compact ? body.slice(0, 2000) : body}
      </MessageResponse>
    </div>
  );
}

/** A column's name as a spreadsheet says it: A, Z, AA. */
function columnName(index: number): string {
  let name = '';
  for (let rest = index + 1; rest > 0; rest = Math.floor((rest - 1) / 26))
    name = String.fromCharCode(65 + ((rest - 1) % 26)) + name;
  return name;
}

/** A CSV or XLSX file as a table: its first row as the header, a sheet at a time. */
function TableViewer({
  content,
  path,
  compact,
}: {
  content: ArtifactContent;
  path: string;
  compact: boolean;
}) {
  const xlsx = /\.xlsx$/i.test(path);
  const [sheets, setSheets] = useState<Sheet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState(0);
  const text = useMemo(
    () => (xlsx ? null : new TextDecoder().decode(bytesOf(content))),
    [content, xlsx],
  );
  useEffect(() => {
    let live = true;
    setError(null);
    if (text !== null) {
      setSheets([
        {
          name: basename(path),
          rows: parseCsvRows(text, /\.tsv$/i.test(path) ? '\t' : csvSeparator(text)).slice(
            0,
            MAX_ROWS,
          ),
        },
      ]);
      return;
    }
    readXlsx(bytesOf(content)).then(
      (value) => live && setSheets(value),
      (reason: unknown) =>
        live && setError(reason instanceof Error ? reason.message : String(reason)),
    );
    return () => {
      live = false;
    };
  }, [content, path, text]);
  if (error !== null)
    return compact ? null : (
      <InlineMessage title="This table cannot be read." tone="error">
        {error}
      </InlineMessage>
    );
  if (sheets === null)
    return <div aria-busy="true" className="size-full" data-common-viewer="table" />;
  const sheet = sheets[shown] ?? sheets[0];
  const rows = (sheet?.rows ?? []).slice(0, compact ? 6 : MAX_ROWS);
  const [header, ...body] = rows;
  const columns = Math.max(0, ...rows.map((row) => row.length));
  // Cells keyed as a spreadsheet names them: A, B… and the row's number.
  const letters = Array.from({ length: columns }, (_, column) => columnName(column));
  const cells = (row: string[]) =>
    letters.map((letter, column) => ({ letter, value: row[column] ?? '' }));
  const numbered = body.map((row, index) => ({ number: String(index + 2), row }));
  return (
    <div className="flex size-full min-h-0 flex-col" data-common-viewer="table">
      {!compact && sheets.length > 1 && (
        <div
          aria-label="Sheets"
          className="flex shrink-0 gap-1 border-hairline border-b px-3 py-1.5"
          role="tablist"
        >
          {sheets.map((entry, index) => (
            <Button
              aria-selected={index === shown}
              key={entry.name}
              onClick={() => setShown(index)}
              role="tab"
              size="xs"
              type="button"
              variant={index === shown ? 'secondary' : 'ghost'}
            >
              {entry.name}
            </Button>
          ))}
        </div>
      )}
      <div className={cn('min-h-0 flex-1', compact ? 'overflow-hidden' : 'overflow-auto')}>
        <table className="w-max min-w-full border-collapse text-xs tabular-nums">
          {header !== undefined && (
            <thead className="sticky top-0 bg-card">
              <tr>
                {cells(header).map((cell) => (
                  <th
                    className="border border-hairline px-2 py-1 text-left font-semibold"
                    key={cell.letter}
                  >
                    {cell.value}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {numbered.map(({ number, row }) => (
              <tr key={number}>
                {cells(row).map((cell) => (
                  <td className="border border-hairline px-2 py-1" key={cell.letter}>
                    {cell.value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
